import type { ClientEvent, ServerEvent } from "@glass/shared";
import { api } from "./api";

export type Listener = (e: ServerEvent) => void;

export class ChatSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private url: string;
  private retry = 0;
  private destroyed = false;

  constructor(kind: "chat" | "audio" | "presence", id: string) {
    const origin = api.origin();
    const wsOrigin = origin.replace(/^http/, "ws");
    const token = encodeURIComponent(api.getToken() || "");
    this.url = `${wsOrigin}/ws/${kind}/${encodeURIComponent(id)}?token=${token}`;
  }

  connect() {
    if (this.destroyed) return;
    this.ws = new WebSocket(this.url);
    this.ws.onopen = () => {
      this.retry = 0;
    };
    this.ws.onmessage = (m) => {
      try {
        const e = JSON.parse(typeof m.data === "string" ? m.data : "") as ServerEvent;
        this.listeners.forEach((l) => l(e));
      } catch { /* empty */ }
    };
    this.ws.onclose = () => {
      if (this.destroyed) return;
      this.retry = Math.min(this.retry + 1, 8);
      setTimeout(() => this.connect(), Math.min(15000, 500 * 2 ** this.retry));
    };
    this.ws.onerror = () => {
      try { this.ws?.close(); } catch { /* empty */ }
    };
  }

  send(e: ClientEvent) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(e));
    }
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  close() {
    this.destroyed = true;
    try { this.ws?.close(); } catch { /* empty */ }
  }
}
